// ════════════════════════════════════════════════════════════════════════════
// components/CRM/EtapaWorkflow/NegociacionForm.jsx
// Formulario para gestionar la etapa de negociación en el CRM
// ════════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Slider,
  Divider,
  Chip,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Send as SendIcon,
  TrendingDown as TrendingDownIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useFiscal } from '../../../hooks/useFiscal';

const NegociacionForm = ({ oportunidad, onGuardado, onTransicionCompleta }) => {
  const fiscal = useFiscal();
  // ═══════════════════════════════════════════════════════════════════════
  // STATE DEL COMPONENTE
  // ═══════════════════════════════════════════════════════════════════════
  const [guardando, setGuardando] = useState(false);
  const [partidas, setPartidas] = useState([]);
  
  const [datos, setDatos] = useState({
    descuentoSolicitado: 0,
    puntos_discusion: '',
    competencia_activa: false,
    nombre_competencia: '',
    oferta_competencia: ''
  });

  // ═══════════════════════════════════════════════════════════════════════
  // DATOS DERIVADOS DE LA OPORTUNIDAD
  // ═══════════════════════════════════════════════════════════════════════
  const propuesta = oportunidad.propuesta || {};
  const montoOriginal = parseFloat(propuesta.total) || 0;
  const subtotalPropuesta = parseFloat(propuesta.subtotal) || 0;
  const impuestosPropuesta = parseFloat(propuesta.impuestos) || 0;
  
  // Formato de moneda
  const formatoMoneda = new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: fiscal.moneda?.codigo || 'DOP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  // Número de cotización
  const numeroCotizacion = propuesta.numero_cotizacion || 
                          `COT-${new Date().getFullYear()}-${String(oportunidad.id).padStart(4, '0')}`;

  // ═══════════════════════════════════════════════════════════════════════
  // EFFECT: Inicializar partidas cuando cambia la propuesta
  // ═══════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (propuesta.lineas && Array.isArray(propuesta.lineas)) {
      const iniciales = propuesta.lineas.map((item, index) => ({
        linea_id: item.partida_id || item.id || index,
        descripcion: item.descripcion || item.nombre || 'Partida sin nombre',
        unidad: item.unidad || 'und',
        cantidad: parseFloat(item.cantidad) || 0,
        precio_original: parseFloat(item.precio_unitario) || 0,
        precio_pactado: parseFloat(item.precio_unitario) || 0,
        total_pactado: parseFloat(item.total) || parseFloat(item.subtotal) || 0,
        costo_interno: parseFloat(item.costo_unitario) || 0,
        modificado: false
      }));
      setPartidas(iniciales);
    } else {
      setPartidas([]);
    }
  }, [propuesta.lineas]);

  // ═══════════════════════════════════════════════════════════════════════
  // MANEJADORES DE EVENTOS
  // ═══════════════════════════════════════════════════════════════════════
  const handlePrecioChange = (lineaId, nuevoPrecio) => {
    const precio = parseFloat(nuevoPrecio) || 0;
    setPartidas(prevPartidas => 
      prevPartidas.map(item => {
        if (item.linea_id === lineaId) {
          const nuevoTotal = precio * item.cantidad;
          return {
            ...item,
            precio_pactado: precio,
            total_pactado: nuevoTotal,
            modificado: precio !== item.precio_original
          };
        }
        return item;
      })
    );
  };

  const handleDescuentoChange = (event, newValue) => {
    setDatos(prev => ({ ...prev, descuentoSolicitado: newValue }));
  };

  const handleInputChange = (field) => (event) => {
    setDatos(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleCompetenciaToggle = (event) => {
    setDatos(prev => ({ 
      ...prev, 
      competencia_activa: event.target.checked,
      nombre_competencia: event.target.checked ? prev.nombre_competencia : '',
      oferta_competencia: event.target.checked ? prev.oferta_competencia : ''
    }));
  };
const subtotalPartidas = partidas.reduce((acc, curr) => acc + (Number(curr.total_pactado) || 0), 0);
const porcentajeDescuento = Number(datos.descuentoSolicitado || 0);
const montoDescuento = subtotalPartidas * (porcentajeDescuento / 100);  // ← Calcular % del subtotal
const subtotalConDescuento = subtotalPartidas - montoDescuento;
const impuestoCalculado = subtotalConDescuento * 0.18;
const totalFinalCalculado = subtotalConDescuento + impuestoCalculado;

const handleGuardar = async () => {
  setGuardando(true);
  
  // Log de entrada para monitorear los cálculos vivos
  console.log("🚀 [FRONTEND] Iniciando proceso de guardado...");
  console.log("📊 [VALORES CALCULADOS]:", {
    subtotal: subtotalPartidas,
    descuento: montoDescuento,
    itbis: impuestoCalculado,
    totalFinal: totalFinalCalculado
  });

  try {
    const costoTotal = partidas.reduce((acc, curr) => 
      acc + (Number(curr.costo_interno || 0) * Number(curr.cantidad || 0)), 0);
    
    const margenRealCalculado = totalFinalCalculado > 0 
      ? ((totalFinalCalculado - costoTotal) / totalFinalCalculado) * 100 
      : 0;

    const datosParaEnviar = {
      valor_estimado: totalFinalCalculado, // Columna numeric principal
      negociacion: {
        ...oportunidad.negociacion,
        monto_final: totalFinalCalculado, // ← 🎯 CLAVE: Este es el que el backend busca
        subtotal_negociado: subtotalConDescuento,
        itbis_negociado: impuestoCalculado,
        descuento_aplicado: montoDescuento,
        puntos_discusion: datos.puntos_discusion,
        competencia_activa: datos.competencia_activa,
        nombre_competencia: datos.nombre_competencia,
        oferta_competencia: datos.oferta_competencia,
        fecha_negociacion: new Date().toISOString()
      },
      cierre_detalle: {
        monto_final: totalFinalCalculado,
        margen_real: margenRealCalculado.toFixed(2),
        partidas_pactadas: partidas.map(p => ({
          linea_id: p.linea_id,
          descripcion: p.descripcion,
          cantidad: p.cantidad,
          precio_original: p.precio_original,
          precio_pactado: p.precio_pactado,
          total_pactado: p.total_pactado,
          modificado: p.precio_pactado !== p.precio_original
        }))
      }
    };

    console.log("📤 [DATOS ENVIADOS A DB]:", datosParaEnviar);
    console.log("🎯 [MONTO FINAL NEGOCIADO]:", totalFinalCalculado);

    if (onGuardado) {
      await onGuardado(datosParaEnviar);
      console.log("✅ [BACKEND] onGuardado ejecutado con éxito");
    }
    
    toast.success("✅ Negociación sincronizada");

    if (onTransicionCompleta) {
      console.log("🔄 [PIPELINE] Notificando actualización...");
      onTransicionCompleta();
    }
  } catch (error) {
    console.error('❌ [ERROR]:', error);
    toast.error('Error al sincronizar');
  } finally {
    setGuardando(false);
  }
};

  // ═══════════════════════════════════════════════════════════════════════
  // RENDERIZADO DEL COMPONENTE
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ p: 3 }}>
      {/* ════════════════════════════════════════════════════════════════ */}
      {/* ENCABEZADO */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <Box sx={{ mb: 3, pb: 2, borderBottom: '2px solid', borderColor: 'primary.main' }}>
        <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TrendingDownIcon sx={{ fontSize: 32 }} />
          Negociación Comercial
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Cotización: <strong>{numeroCotizacion}</strong>
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          Cliente: <strong>{oportunidad.prospecto?.nombre || oportunidad.prospecto?.empresa || 'N/A'}</strong>
          {oportunidad.prospecto?.email && ` • ${oportunidad.prospecto.email}`}
        </Typography>
      </Box>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* RESUMEN DE PROPUESTA ORIGINAL */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#f0f9ff', border: '2px solid', borderColor: 'primary.main', borderRadius: 2 }}>
        <Typography variant="h6" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon />
          Propuesta Original
        </Typography>
        <Grid container spacing={3}>
          <Grid size xs={12} md={4}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Monto Total
              </Typography>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {formatoMoneda.format(montoOriginal)}
              </Typography>
            </Box>
          </Grid>
          
          <Grid size xs={6} md={4}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Subtotal
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatoMoneda.format(subtotalPropuesta)}
              </Typography>
            </Box>
          </Grid>
          
         {/* Sección de Impuesto */}
<Grid size xs={6} md={4}>
  <Box sx={{ textAlign: 'center', p: 2 }}>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      Impuesto (18%) 💰
    </Typography>
    <Typography variant="h6" fontWeight="bold" color="success.main">
      {formatoMoneda.format(impuestosPropuesta)}
    </Typography>
  </Box>
</Grid>

{/* Sección de Total Negociado - ESTE SÍ CAMBIA */}
<Grid size xs={12} md={4}>
  <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      Total Negociado 💎
    </Typography>
    <Typography variant="h4" color="success.main" fontWeight="bold">
      {formatoMoneda.format(totalFinalCalculado)}
    </Typography>
  </Box>
</Grid>
        </Grid>
      </Paper>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* TABLA DE PARTIDAS PARA NEGOCIACIÓN */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {partidas.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalculateIcon />
            Partidas para Negociación
          </Typography>
          
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, mt: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.100' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%' }}>Unidad</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%' }}>Cant.</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', width: '15%' }}>Precio Original</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', width: '20%' }}>Precio Pactado</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', width: '15%' }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {partidas.map((item) => (
                  <TableRow 
                    key={item.linea_id} 
                    sx={{ 
                      bgcolor: item.modificado ? 'action.hover' : 'inherit',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={item.modificado ? 'bold' : 'regular'}>
                        {item.descripcion}
                      </Typography>
                      {item.codigo && (
                        <Typography variant="caption" color="text.secondary">
                          Código: {item.codigo}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">{item.unidad}</TableCell>
                    <TableCell align="center">{item.cantidad.toLocaleString()}</TableCell>
                    <TableCell align="right">{formatoMoneda.format(item.precio_original)}</TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={item.precio_pactado}
                        onChange={(e) => handlePrecioChange(item.linea_id, e.target.value)}
                        InputProps={{ 
                          startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                          sx: { width: 120 }
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            bgcolor: item.modificado ? 'warning.light' : 'grey.100'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: item.modificado ? 'bold' : 'regular' }}>
                      {formatoMoneda.format(item.total_pactado)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="info.dark">
              💡 <strong>Tip:</strong> Modifica el "Precio Pactado" para negociar individualmente cada partida. 
              Los cambios se reflejarán en el monto final.
            </Typography>
          </Box>
        </Paper>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* DESCUENTO GLOBAL */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingDownIcon />
          Descuento Global Solicitado
        </Typography>
        
        <Box sx={{ px: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Porcentaje de descuento:
            </Typography>
            <Chip 
              label={`${datos.descuentoSolicitado}%`}
              color={datos.descuentoSolicitado > 15 ? "error" : datos.descuentoSolicitado > 5 ? "warning" : "success"}
              size="medium"
              sx={{ fontSize: '1.1rem', fontWeight: 'bold', px: 2 }}
            />
          </Box>
          
          <Slider
            value={datos.descuentoSolicitado}
            onChange={handleDescuentoChange}
            min={0}
            max={30}
            step={1}
            marks={[
              { value: 0, label: '0%' },
              { value: 5, label: '5%' },
              { value: 10, label: '10%' },
              { value: 15, label: '15%' },
              { value: 20, label: '20%' },
              { value: 25, label: '25%' },
              { value: 30, label: '30%' }
            ]}
            valueLabelDisplay="auto"
            sx={{ 
              '& .MuiSlider-thumb': {
                bgcolor: datos.descuentoSolicitado > 15 ? 'error.main' : 
                         datos.descuentoSolicitado > 5 ? 'warning.main' : 'success.main'
              },
              '& .MuiSlider-track': {
                bgcolor: datos.descuentoSolicitado > 15 ? 'error.main' : 
                         datos.descuentoSolicitado > 5 ? 'warning.main' : 'success.main'
              }
            }}
          />
          
          <Box sx={{ mt: 2, p: 2, bgcolor: datos.descuentoSolicitado > 15 ? 'error.light' : 
                                    datos.descuentoSolicitado > 5 ? 'warning.light' : 'success.light',
                  borderRadius: 1, borderLeft: '4px solid', 
                  borderColor: datos.descuentoSolicitado > 15 ? 'error.main' : 
                               datos.descuentoSolicitado > 5 ? 'warning.main' : 'success.main' }}>
            <Typography variant="body2">
              {datos.descuentoSolicitado === 0 && "✅ Sin descuento aplicado"}
              {datos.descuentoSolicitado > 0 && datos.descuentoSolicitado <= 5 && "👍 Descuento razonable dentro de márgenes aceptables"}
              {datos.descuentoSolicitado > 5 && datos.descuentoSolicitado <= 15 && "⚠️ Descuento significativo - Requiere aprobación gerencial si supera el 10%"}
              {datos.descuentoSolicitado > 15 && "🚨 Descuento alto - Requiere justificación y aprobación de gerencia"}
            </Typography>
          </Box>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={3}>
          <Grid size xs={12} md={6}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
              <Typography variant="body2" color="error.dark" gutterBottom>
                Descuento en monto:
              </Typography>
              <Typography variant="h5" color="error" fontWeight="bold">
                - {formatoMoneda.format(montoDescuento)}
              </Typography>
            </Paper>
          </Grid>
          <Grid size xs={12} md={6}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
              <Typography variant="body2" color="success.dark" gutterBottom>
                Monto final con descuento:
              </Typography>
              <Typography variant="h5" color="success.main" fontWeight="bold">
                {formatoMoneda.format(totalFinalCalculado)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* PUNTOS DE DISCUSIÓN */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon />
          Puntos de Discusión y Condiciones
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={5}
          label="Temas discutidos, objeciones del cliente, condiciones especiales, plazos de entrega, etc."
          value={datos.puntos_discusion}
          onChange={handleInputChange('puntos_discusion')}
          placeholder="Ej: Cliente solicita 15% descuento por pago anticipado. Plazo de entrega extendido a 45 días. Incluir garantía extendida..."
          variant="outlined"
          sx={{ mt: 1 }}
        />
      </Paper>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* INFORMACIÓN DE COMPETENCIA */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon />
          Información de Competencia
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={datos.competencia_activa}
              onChange={handleCompetenciaToggle}
              color="error"
            />
          }
          label={
            <Typography variant="body1" fontWeight="bold">
              {datos.competencia_activa ? 'Competencia identificada' : '¿Hay competencia activa?'}
            </Typography>
          }
          sx={{ mb: 2 }}
        />
        
        {datos.competencia_activa && (
          <Grid container spacing={2}>
           <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Nombre de la competencia"
                value={datos.nombre_competencia}
                onChange={handleInputChange('nombre_competencia')}
                placeholder="Ej: Constructora XYZ, Proveedora ABC"
                variant="outlined"
              />
            </Grid>
           <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Oferta de la competencia"
                value={datos.oferta_competencia}
                onChange={handleInputChange('oferta_competencia')}
                placeholder="Ej: $450,000 con 60 días de plazo"
                variant="outlined"
              />
            </Grid>
            <Grid size xs={12}>
              <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1, mt: 1 }}>
                <Typography variant="body2" color="error.dark">
                  📌 <strong>Importante:</strong> Documentar la competencia ayuda a ajustar estrategias comerciales y mejorar futuras propuestas.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* RESUMEN FINAL */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <Paper sx={{ p: 3, mb: 4, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid', borderColor: 'grey.300' }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          📊 Resumen de Negociación
        </Typography>
        <Grid container spacing={2}>
          <Grid size xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Monto original:
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {formatoMoneda.format(montoOriginal)}
            </Typography>
          </Grid>
          
          <Grid size xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Descuento aplicado:
            </Typography>
            <Typography 
              variant="body1" 
              fontWeight="bold" 
              color={datos.descuentoSolicitado > 0 ? 'error.main' : 'text.primary'}
            >
              {datos.descuentoSolicitado}% ({formatoMoneda.format(montoDescuento)})
            </Typography>
          </Grid>
          
          <Grid size xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Monto final:
            </Typography>
            <Typography variant="h6" color="primary" fontWeight="bold">
              {formatoMoneda.format(totalFinalCalculado)}
            </Typography>
          </Grid>
          
          <Grid size xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Competencia:
            </Typography>
            <Chip 
              label={datos.competencia_activa ? datos.nombre_competencia || "Identificada" : "No reportada"}
              color={datos.competencia_activa ? "error" : "success"}
              size="small"
              icon={datos.competencia_activa ? <BusinessIcon /> : undefined}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* BOTONES DE ACCIÓN */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          variant="outlined"
          size="large"
          onClick={() => onTransicionCompleta?.()}
          disabled={guardando}
          sx={{ px: 4, py: 1.5 }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<SendIcon />}
          onClick={handleGuardar}
          disabled={guardando}
          sx={{ 
            px: 4, 
            py: 1.5,
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
            boxShadow: 3
          }}
        >
          {guardando ? 'Guardando...' : 'Guardar y Continuar Negociación'}
        </Button>
      </Box>
    </Box>
  );
};

export default NegociacionForm;